<template>
  <div class="flex flex-col" data-cy="do-reasons-table">
    <data-orchestration-history-tabs v-model="selectedTab" />
    <data-orchestration-history-breadcrumbs />
    <div
      class="history-table-border flex max-h-110 flex-col space-y-4 overflow-y-auto rounded-b border p-4 whitespace-nowrap"
    >
      <div class="grid grid-cols-6 items-center font-semibold">
        <span></span>
        <span>{{ $t("ORCHESTRATION.SOURCE") }}</span>
        <span class="col-span-2">{{ $t("ORCHESTRATION.ATTRIBUTE") }}</span>
        <span class="justify-self-end">
          {{ $t("ORCHESTRATION.EXPECTED_VALUE") }}
        </span>
        <span class="justify-self-end">
          {{ $t("ORCHESTRATION.RETURNED_VALUE") }}
        </span>
      </div>
      <div ref="historyRowsWrapper">
        <data-orchestration-history-row
          v-for="log in logs"
          :key="`log-${log.started_at || log.issued_at}`"
          :log="log"
          :is-last-block-ran="deepEqual(newestAutomatedHistoryLog, log)"
          :active-tab="selectedTab"
        />
      </div>
      <div
        v-if="messageForNoLogs"
        class="flex items-center justify-center py-4"
        data-cy="no-logs-message"
      >
        {{ messageForNoLogs }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, onUpdated, ref } from "vue";
import { useI18n } from "vue-i18n";

import { deepEqual } from "fast-equals";
import compact from "lodash/compact";
import { storeToRefs } from "pinia";

import { useUnderwritingLogs } from "@/hooks/underwriting";
import useApplicationsStore from "@/stores/applications";
</script>

<script setup lang="ts">
import DataOrchestrationHistoryBreadcrumbs from "@/components/deals/dataOrchestration/status/DataOrchestrationHistoryBreadcrumbs.vue";
import DataOrchestrationHistoryRow from "@/components/deals/dataOrchestration/status/DataOrchestrationHistoryRow.vue";
import DataOrchestrationHistoryTabs from "@/components/deals/dataOrchestration/status/DataOrchestrationHistoryTabs.vue";

const { historyLogs, newestAutomatedHistoryLog } = useUnderwritingLogs();
const { t } = useI18n();
const applicationsStore = useApplicationsStore();
const { activeDataOrchestrationLog } = storeToRefs(applicationsStore);
const NO_DATA_MESSAGES = {
  [t("ORCHESTRATION.CONDITION_NOT_MET")]: t("ORCHESTRATION.NO_UNMET_STEPS"),
  [t("ORCHESTRATION.CONDITION_MET")]: t("ORCHESTRATION.NO_MET_STEPS"),
};

const selectedTab = ref(t("ORCHESTRATION.CONDITION_NOT_MET"));
const historyRowsWrapper = ref<HTMLDivElement | null>(null);
const historyRowsCount = ref(0);

const allTemplateSteps = computed(
  () => activeDataOrchestrationLog.value?.template?.content?.steps || []
);

const logs = computed(() =>
  historyLogs.value
    .slice()
    .reverse()
    .filter((log) => {
      const step = allTemplateSteps.value.find(
        (step) => step.id === log.step_id
      );
      const outcomes = step?.outcomes || [];
      if (selectedTab.value === t("COMMON.ALL")) {
        return outcomes;
      }
      const outcomesToInclude = compact(
        log.context?.log?.reduce<Array<string>>(
          (acc, { outcome, outcome_result }) => {
            if (
              (selectedTab.value === t("ORCHESTRATION.CONDITION_NOT_MET") &&
                outcome_result === false) ||
              (selectedTab.value === t("ORCHESTRATION.CONDITION_MET") &&
                outcome_result === true)
            ) {
              if (!acc.includes(outcome)) {
                acc.push(outcome);
              }
            }
            return acc;
          },
          []
        )
      );

      return !!outcomes.filter(({ outcome }) =>
        outcomesToInclude.includes(outcome)
      ).length;
    })
);

const messageForNoLogs = computed(() => {
  if (historyRowsCount.value) {
    return "";
  }
  return NO_DATA_MESSAGES[selectedTab.value] || t("ORCHESTRATION.NO_STEPS");
});

onUpdated(() => {
  const newHistoryRowsCount = historyRowsWrapper.value?.childElementCount || 0;
  if (newHistoryRowsCount !== historyRowsCount.value) {
    historyRowsCount.value = newHistoryRowsCount;
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.history-table-border {
  @apply border-gray-200;
}
</style>
